import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StoryCard from "@/components/StoryCard";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["all", "World", "Politics", "Tech", "Business", "Science", "Health"];

export default function ArchivePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stories, setStories] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [sourceKey, setSourceKey] = useState("all");
  const [sort, setSort] = useState("recent");
  const [search, setSearch] = useState("");
  const [bookmarkIds, setBookmarkIds] = useState(new Set());

  const loadStories = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/news/list", {
        params: { category, source: sourceKey, search, sort, limit: 200 },
      });
      setStories(r.data);
    } catch {
      toast.error("Failed to load archive");
    } finally {
      setLoading(false);
    }
  }, [category, sourceKey, search, sort]);

  useEffect(() => {
    api.get("/news/sources").then((r) => setSources(r.data.sources)).catch(() => {});
  }, []);

  useEffect(() => { loadStories(); }, [loadStories]);

  useEffect(() => {
    if (!user) { setBookmarkIds(new Set()); return; }
    api.get("/bookmarks/ids").then((r) => setBookmarkIds(new Set(r.data.ids))).catch(() => {});
  }, [user]);

  const toggleBookmark = async (storyId) => {
    const isBookmarked = bookmarkIds.has(storyId);
    try {
      if (isBookmarked) {
        await api.delete(`/bookmarks/${storyId}`);
        const next = new Set(bookmarkIds); next.delete(storyId); setBookmarkIds(next);
      } else {
        await api.post("/bookmarks", { story_id: storyId });
        const next = new Set(bookmarkIds); next.add(storyId); setBookmarkIds(next);
      }
    } catch { toast.error("Action failed"); }
  };

  const requireAuth = () => { toast.error("Sign in to save"); nav("/login"); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b-2 border-stone-900 pb-6 mb-8">
        <div className="font-mono-head text-xs uppercase tracking-[0.3em] text-stone-500 mb-2">
          // Full Index · Searchable Vault
        </div>
        <h1 className="font-serif-head text-4xl sm:text-5xl text-stone-900">The Archive</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar filters */}
        <aside className="lg:col-span-3 space-y-6">
          <div>
            <div className="font-mono-head text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">
              · Sort
            </div>
            <div className="flex flex-col gap-1">
              {[["recent", "Most Recent"], ["heat", "Hottest"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`sort-${k}`}
                  onClick={() => setSort(k)}
                  className={`text-left font-mono-head text-xs uppercase tracking-widest px-3 py-2 border ${
                    sort === k ? "border-stone-900 bg-stone-900 text-stone-50" : "border-stone-300 text-stone-700 hover:border-stone-900"
                  }`}
                >{label}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono-head text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">
              · Category
            </div>
            <div className="flex flex-col gap-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`archive-cat-${c}`}
                  onClick={() => setCategory(c)}
                  className={`text-left font-mono-head text-xs uppercase tracking-widest px-3 py-2 border ${
                    category === c ? "border-stone-900 bg-stone-900 text-stone-50" : "border-stone-300 text-stone-700 hover:border-stone-900"
                  }`}
                >{c === "all" ? "All" : c}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono-head text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">
              · Source
            </div>
            <select
              data-testid="source-filter"
              value={sourceKey}
              onChange={(e) => setSourceKey(e.target.value)}
              className="w-full bg-stone-50 border border-stone-300 font-mono-head text-xs uppercase tracking-widest px-3 py-2 focus:outline-none focus:border-stone-900"
            >
              <option value="all">All Sources</option>
              {sources.map((s) => (
                <option key={s.key} value={s.key}>{s.name}</option>
              ))}
            </select>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          <div className="flex items-center gap-2 border-b-2 border-stone-900 mb-6 pb-1">
            <Search className="w-4 h-4 text-stone-700" />
            <input
              data-testid="archive-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search archive…"
              className="flex-1 bg-transparent font-mono-head text-base py-3 px-0 focus:outline-none placeholder-stone-400"
            />
          </div>

          {loading ? (
            <div className="py-20 text-center font-mono-head text-xs uppercase tracking-widest text-stone-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Querying vault…
            </div>
          ) : stories.length === 0 ? (
            <p className="font-mono-head text-sm uppercase tracking-widest text-stone-500 py-20 text-center">
              No matches in archive.
            </p>
          ) : (
            <>
              <div className="font-mono-head text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-4">
                {stories.length} result{stories.length !== 1 ? "s" : ""}
              </div>
              {stories.map((s, i) => (
                <StoryCard
                  key={s.id}
                  story={s}
                  index={i}
                  isBookmarked={bookmarkIds.has(s.id)}
                  onToggleBookmark={toggleBookmark}
                  requireAuth={!user ? requireAuth : null}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
